import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const API = (typeof window !== "undefined" && window.__PINLAYR_EMBED__ && window.__PINLAYR_EMBED__.apiBase) || process.env.REACT_APP_BACKEND_URL;
const EMBED = (typeof window !== "undefined" && window.__PINLAYR_EMBED__) || null;
const TOKEN_KEY = "pinlayr_gate_token";

// Decode the (non-secret) payload of a reviewer/member session JWT to read identity.
const decodeReviewer = (jwt) => {
  try {
    const p = JSON.parse(atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (p && (p.typ === "reviewer" || p.typ === "member")) return p;
  } catch (_e) { /* ignore */ }
  return null;
};
const REVIEWER = EMBED && EMBED.reviewerToken ? decodeReviewer(EMBED.reviewerToken) : null;
const ME_KEY = "pinlayr_comments_me";
const SEEN_KEY = "pinlayr_comments_seen";
const SOUND_KEY = "pinlayr_comments_sound";
const HIDE_ROUTES = []; // gate applies to all routes; comments too

const IFRAME_QUERY_FLAG = "__pinlayr_in_preview_iframe__";
export const isInPreviewIframe =
  typeof window !== "undefined" &&
  (window.self !== window.top ||
    (typeof window.location !== "undefined" &&
      window.location.search.includes(IFRAME_QUERY_FLAG)));

// Extract @mention tokens from a message. Returns list of names typed after @.
// Names can contain letters, digits, spaces (single) — we stop at newline / punctuation.
export const parseMentions = (text) => {
  if (!text) return [];
  const re = /@([A-Za-z][A-Za-z0-9]*(?:\s[A-Za-z][A-Za-z0-9]*)?)/g;
  const out = [];
  let m;
  while ((m = re.exec(text))) out.push(m[1]);
  return out;
};

// Play a subtle two-note chime via Web Audio (no asset needed)
export const playChime = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ac = new AC();
    const now = ac.currentTime;
    const notes = [
      { f: 880, start: 0, dur: 0.28, vol: 0.14 },
      { f: 1320, start: 0.14, dur: 0.34, vol: 0.1 },
    ];
    notes.forEach(({ f, start, dur, vol }) => {
      const osc = ac.createOscillator();
      const g = ac.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.001, now + start);
      g.gain.exponentialRampToValueAtTime(vol, now + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(g).connect(ac.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    });
    setTimeout(() => ac.close(), 800);
  } catch (_e) {
    // ignore — audio blocked or unavailable
  }
};

// Trello-inspired palette for reviewer bubbles — vivid, saturated, high-contrast
// on both light and dark surfaces. Order intentionally curated so the first
// swatch (Sunday yellow) is the brand default.
export const AUTHOR_COLORS = [
  "#F2D600", // Sunday yellow / Trello yellow
  "#0079BF", // Trello blue
  "#00C2E0", // Trello sky
  "#61BD4F", // Trello green
  "#FF9F1A", // Trello orange
  "#EB5A46", // Trello red
  "#C377E0", // Trello purple
  "#FF78CB", // Trello pink
];

const uid = () =>
  (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
  Math.random().toString(36).slice(2) + Date.now().toString(36);

const pickColor = (seed) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AUTHOR_COLORS[Math.abs(h) % AUTHOR_COLORS.length];
};

const SUNDAY_YELLOW = "#F5D400";
const CLIENT_BLUE = "#0B7BC7"; // second Trello-style color after yellow

// Role is set by PasswordGate after a successful login: "owner" (Owner
// password) or "client" (Client password). We only use it to pick a
// default identity on a fresh browser — permissions are identical.
const ROLE_KEY = "pinlayr_gate_role";

const readRole = () => {
  try { return localStorage.getItem(ROLE_KEY); } catch (_e) { return null; }
};

const readMe = () => {
  // Accountless reviewer: identity comes from the signed session (name/color),
  // so they never see the guest name prompt.
  if (REVIEWER) return { id: REVIEWER.reviewerId, name: REVIEWER.name, color: REVIEWER.color };
  let stored = null;
  try {
    const raw = localStorage.getItem(ME_KEY);
    if (raw) stored = JSON.parse(raw);
  } catch (_e) {
    // ignore malformed localStorage
  }
  // If we already have an identity, ALWAYS return it — reviewers are free
  // to rename/recolor themselves and that choice persists across sessions
  // regardless of the password they logged in with.
  if (stored) return stored;
  // Fresh browser — seed a role-appropriate default so the reviewer skips
  // the "pick a name" prompt on first comment. Owner (yellow),
  // Client (blue). Either identity is fully editable later.
  const role = readRole();
  if (role === "owner") {
    const me = {
      id:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : String(Date.now()),
      name: "Sunday",
      color: SUNDAY_YELLOW,
    };
    try { localStorage.setItem(ME_KEY, JSON.stringify(me)); } catch (_e) { /* ignore */ }
    return me;
  }
  if (role === "client") {
    const me = {
      id:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : String(Date.now()),
      name: "Client",
      color: CLIENT_BLUE,
    };
    try { localStorage.setItem(ME_KEY, JSON.stringify(me)); } catch (_e) { /* ignore */ }
    return me;
  }
  return null;
};

const persistMe = (me) => {
  try {
    localStorage.setItem(ME_KEY, JSON.stringify(me));
  } catch (_e) {
    // ignore storage quota / privacy-mode errors
  }
};

const currentViewport = () => {
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
};

const CommentsCtx = createContext(null);

export const useComments = () => {
  const ctx = useContext(CommentsCtx);
  if (!ctx) throw new Error("useComments must be used inside <CommentsProvider>");
  return ctx;
};

const authHeaders = () => {
  if (EMBED) {
    const h = { "X-Pinlayr-Project": EMBED.projectKey };
    if (EMBED.reviewerToken) h["X-Pinlayr-Reviewer"] = EMBED.reviewerToken;
    return h;
  }
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const ROUTE_LABELS = {
  "/": "Landing",
  "/demo/landing": "Landing",
  "/demo/article": "Article",
  "/demo/product": "Product",
};

// Max attachment file size (per image) — 2 MB
export const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_MSG = 4;

export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// Read the initial review-mode flag from the URL (?review=1). Persisted
// there so shared links land directly in the frozen review view.
const readInitialReviewMode = () => {
  try {
    return new URLSearchParams(window.location.search).get("review") === "1";
  } catch (_e) {
    return false;
  }
};

// Read the initial deep-link ticket id from the URL (?ticket=<id>). Used
// with `?review=1` to open a specific ticket panel on share.
const readInitialTicketId = () => {
  try {
    return new URLSearchParams(window.location.search).get("ticket") || null;
  } catch (_e) {
    return null;
  }
};

// Snapshot every sticky/fixed element outside the comments UI and force
// them into normal document flow via `position: static`. Framer-motion
// transforms and CSS animations get paused via a global `.review-mode`
// class (see index.css). Returns the list of touched elements so we can
// restore them cleanly when the reviewer exits review mode.
const freezePageStickies = () => {
  const frozen = [];
  document.querySelectorAll("*").forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.closest("[data-comments-ui]")) return;
    if (el === document.documentElement || el === document.body) return;
    const cs = window.getComputedStyle(el);
    if (cs.position !== "sticky" && cs.position !== "fixed") return;
    // Snapshot the inline position (usually empty — value came from the
    // stylesheet). Restore reverts to that state on exit.
    const backup = {
      position: el.style.position,
      priority: el.style.getPropertyPriority("position"),
    };
    el.setAttribute("data-review-frozen", JSON.stringify(backup));
    el.style.setProperty("position", "static", "important");
    frozen.push(el);
  });
  return frozen;
};

const unfreezePageStickies = (frozen) => {
  const list = frozen && frozen.length
    ? frozen
    : Array.from(document.querySelectorAll("[data-review-frozen]"));
  list.forEach((el) => {
    const raw = el.getAttribute("data-review-frozen");
    if (raw) {
      try {
        const backup = JSON.parse(raw);
        if (backup.position) {
          el.style.setProperty("position", backup.position, backup.priority || "");
        } else {
          el.style.removeProperty("position");
        }
      } catch (_e) {
        el.style.removeProperty("position");
      }
      el.removeAttribute("data-review-frozen");
    }
  });
};

export const CommentsProvider = ({ children }) => {
  const [me, setMe] = useState(readMe);
  const [comments, setComments] = useState([]);          // scoped to current route
  const [allComments, setAllComments] = useState([]);    // ALL routes (for inbox)
  const [route, setRoute] = useState(window.location.pathname);
  const [mode, setMode] = useState("view"); // "view" | "add" | "hidden"
  const [activeId, setActiveId] = useState(readInitialTicketId);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showDevicePreview, setShowDevicePreview] = useState(false);
  const [namePromptOpen, setNamePromptOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // Review mode: when OFF the page renders as a normal live PDP with only
  // a small "Review this page" CTA visible. When ON the page freezes
  // (Lenis off, stickies → static, animations paused) and the full
  // comments UI appears. Persisted via ?review=1 in the URL.
  const [reviewMode, setReviewMode] = useState(readInitialReviewMode);
  const frozenElsRef = useRef([]);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return localStorage.getItem(SOUND_KEY) !== "0";
    } catch (_e) {
      return true;
    }
  });
  const prevUnreadRef = useRef(0);
  const [seen, setSeen] = useState(() => {
    try {
      const raw = localStorage.getItem(SEEN_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_e) {
      return {};
    }
  });
  const pollRef = useRef(null);
  const [directory, setDirectory] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/comments/mention-directory`, { headers: authHeaders() });
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled) setDirectory(j.directory || []);
      } catch (_e) { /* ignore — mentions still work off known authors */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [viewport, setViewport] = useState(currentViewport);
  useEffect(() => {
    const onResize = () => setViewport(currentViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const persistSeen = useCallback((next) => {
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
    } catch (_e) {
      // ignore quota
    }
  }, []);

  // A comment has unread activity if any reply from someone else was posted
  // after the last time we opened this comment.
  const hasUnread = useCallback(
    (c) => {
      if (!c || c.status === "resolved") return false;
      const lastSeen = seen[c.id];
      const foreignReplies = c.replies.filter((r) => r.author.id !== me?.id);
      const times = foreignReplies.map((r) => r.createdAt);
      // A status change by someone else (e.g. In Progress) also counts as new activity
      if (c.statusChangedAt && c.statusChangedBy && c.statusChangedBy !== me?.id) times.push(c.statusChangedAt);
      if (times.length === 0) return false;
      const latest = times.reduce((a, b) => (new Date(b) > new Date(a) ? b : a));
      if (!lastSeen) return true;
      return new Date(latest).getTime() > new Date(lastSeen).getTime();
    },
    [seen, me]
  );

  // True if any unread reply on this comment @-mentioned the current reviewer
  const mentionsMe = useCallback(
    (c) => {
      if (!me || !c) return false;
      if (!hasUnread(c)) return false;
      const foreignRepliesAfterSeen = c.replies.filter((r) => {
        if (r.author.id === me.id) return false;
        const lastSeen = seen[c.id];
        if (!lastSeen) return true;
        return new Date(r.createdAt).getTime() > new Date(lastSeen).getTime();
      });
      return foreignRepliesAfterSeen.some((r) =>
        (r.mentions || []).includes(me.id)
      );
    },
    [me, seen, hasUnread]
  );

  const unreadCount = useMemo(
    () => comments.filter(hasUnread).length,
    [comments, hasUnread]
  );
  const unreadCountAllRoutes = useMemo(
    () => allComments.filter(hasUnread).length,
    [allComments, hasUnread]
  );
  const mentionedCount = useMemo(
    () => allComments.filter(mentionsMe).length,
    [allComments, mentionsMe]
  );

  // Chime when unread count increases (skip initial mount / initial fetch)
  useEffect(() => {
    if (!soundEnabled) return;
    const prev = prevUnreadRef.current;
    if (prev !== null && unreadCountAllRoutes > prev) {
      // Only chime for genuine new activity (not the very first fetch which
      // sets prev from 0 → whatever). We detect this via loading flag.
      if (!loading) playChime();
    }
    prevUnreadRef.current = unreadCountAllRoutes;
  }, [unreadCountAllRoutes, soundEnabled, loading]);

  // Deep-link sync: keep ?ticket=<id> aligned with `activeId` so any open
  // ticket produces a shareable URL. Uses replaceState to avoid polluting
  // browser history with a new entry for every pin click.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const current = url.searchParams.get("ticket");
      if (activeId && current !== activeId) {
        url.searchParams.set("ticket", activeId);
      } else if (!activeId && current) {
        url.searchParams.delete("ticket");
      } else {
        return; // nothing to update
      }
      const newSearch = url.searchParams.toString();
      const newUrl = url.pathname + (newSearch ? `?${newSearch}` : "") + url.hash;
      window.history.replaceState({}, "", newUrl);
    } catch (_e) {
      // ignore — URL sync is a nicety
    }
  }, [activeId]);

  // On first data load, if a ?ticket=<id> deep link is present, navigate
  // the reviewer to the matching pin's route so the ticket actually mounts.
  const initialTicketRef = useRef(readInitialTicketId());
  useEffect(() => {
    const wanted = initialTicketRef.current;
    if (!wanted || loading) return;
    const found = allComments.find((c) => c.id === wanted);
    if (!found) {
      initialTicketRef.current = null; // give up: not our data
      return;
    }
    // If we're on a different route, hop over. The provider's route effect
    // will re-scope `comments` and mount the ticket.
    if (found.route !== window.location.pathname) {
      window.history.replaceState({}, "", found.route + window.location.search + window.location.hash);
      setRoute(found.route);
    }
    setActiveId(wanted);
    initialTicketRef.current = null;
  }, [allComments, loading]);

  const markSeen = useCallback(
    (commentId) => {
      const c = comments.find((x) => x.id === commentId);
      if (!c) return;
      const latestTs = c.updatedAt || c.createdAt;
      setSeen((prev) => {
        const next = { ...prev, [commentId]: latestTs };
        persistSeen(next);
        return next;
      });
    },
    [comments, persistSeen]
  );

  // Track route changes (react-router doesn't emit an event on Link nav for context outside Router,
  // so we poll history.state via listener + interval fallback)
  useEffect(() => {
    const sync = () => setRoute(window.location.pathname);
    window.addEventListener("popstate", sync);
    // pushState/replaceState don't fire events — monkey-patch to catch programmatic nav
    const wrap = (name) => {
      const orig = window.history[name];
      window.history[name] = function (...args) {
        const r = orig.apply(this, args);
        window.dispatchEvent(new Event("locationchange"));
        return r;
      };
    };
    wrap("pushState");
    wrap("replaceState");
    window.addEventListener("locationchange", sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("locationchange", sync);
    };
  }, []);

  const fetchComments = useCallback(async () => {
    try {
      const [pageRes, allRes] = await Promise.all([
        fetch(`${API}/api/comments?route=${encodeURIComponent(route)}`, {
          headers: authHeaders(),
        }),
        fetch(`${API}/api/comments`, { headers: authHeaders() }),
      ]);
      if (pageRes.ok) setComments(await pageRes.json());
      if (allRes.ok) setAllComments(await allRes.json());
    } catch {
      /* offline — keep last state */
    } finally {
      setLoading(false);
    }
  }, [route]);

  // Fetch on route change + poll every 20s + refetch on focus
  useEffect(() => {
    setLoading(true);
    fetchComments();
    clearInterval(pollRef.current);
    pollRef.current = setInterval(fetchComments, 20000);
    const onFocus = () => fetchComments();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchComments]);

  const ensureAuthor = useCallback(() => {
    if (me) return me;
    setNamePromptOpen(true);
    return null;
  }, [me]);

  const saveName = useCallback((name, colorOverride) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const existing = readMe();
    const author = {
      id: existing?.id || uid(),
      name: trimmed,
      color:
        colorOverride ||
        existing?.color ||
        pickColor(trimmed + Date.now()),
    };
    persistMe(author);
    setMe(author);
    setNamePromptOpen(false);
  }, []);

  const addComment = useCallback(
    async (anchor, message, mentions = [], attachments = [], thumbnail = null) => {
      const author = ensureAuthor();
      if (!author) return null;
      const optimistic = {
        id: `tmp-${uid()}`,
        route,
        anchor,
        author,
        message,
        mentions,
        attachments,
        thumbnail,
        replies: [],
        status: "open",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _pending: true,
      };
      setComments((c) => [...c, optimistic]);
      setAllComments((c) => [...c, optimistic]);
      try {
        const res = await fetch(`${API}/api/comments`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ route, anchor, author, message, mentions, attachments, thumbnail }),
        });
        if (!res.ok) throw new Error(await res.text());
        const saved = await res.json();
        setComments((list) =>
          list.map((c) => (c.id === optimistic.id ? saved : c))
        );
        setAllComments((list) =>
          list.map((c) => (c.id === optimistic.id ? saved : c))
        );
        setMode("view");
        // After creating a ticket, auto-open the full Ticket View so the
        // reviewer can add a description / attachments / replies in context.
        setActiveId(saved.id);
        return saved;
      } catch {
        setComments((list) => list.filter((c) => c.id !== optimistic.id));
        setAllComments((list) => list.filter((c) => c.id !== optimistic.id));
        return null;
      }
    },
    [route, ensureAuthor]
  );

  const addReply = useCallback(
    async (commentId, message, mentions = [], attachments = []) => {
      const author = ensureAuthor();
      if (!author) return null;
      const res = await fetch(`${API}/api/comments/${commentId}/replies`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ author, message, mentions, attachments }),
      });
      if (!res.ok) return null;
      const updated = await res.json();
      setComments((list) => list.map((c) => (c.id === commentId ? updated : c)));
      return updated;
    },
    [ensureAuthor]
  );

  const updateAnchor = useCallback(
    async (commentId, anchor) => {
      const author = ensureAuthor();
      if (!author) return;
      // Optimistic
      setComments((list) =>
        list.map((c) => (c.id === commentId ? { ...c, anchor } : c))
      );
      const res = await fetch(`${API}/api/comments/${commentId}/anchor`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ anchor, author }),
      });
      if (!res.ok) return;
      const updated = await res.json();
      setComments((list) => list.map((c) => (c.id === commentId ? updated : c)));
    },
    [ensureAuthor]
  );

  const setStatus = useCallback(
    async (commentId, status) => {
      const author = ensureAuthor();
      if (!author) return;
      const res = await fetch(`${API}/api/comments/${commentId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ status, author }),
      });
      if (!res.ok) return;
      const updated = await res.json();
      setComments((list) => list.map((c) => (c.id === commentId ? updated : c)));
    },
    [ensureAuthor]
  );

  const setLabels = useCallback(
    async (commentId, labels) => {
      const author = ensureAuthor();
      if (!author) return;
      // Optimistic — chip flips instantly.
      setComments((list) =>
        list.map((c) => (c.id === commentId ? { ...c, labels } : c))
      );
      const res = await fetch(`${API}/api/comments/${commentId}/labels`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ labels, author }),
      });
      if (!res.ok) return;
      const updated = await res.json();
      setComments((list) => list.map((c) => (c.id === commentId ? updated : c)));
    },
    [ensureAuthor]
  );

  const toggleLabel = useCallback(
    async (commentId, label) => {
      const author = ensureAuthor();
      if (!author) return;
      let nextLabels = null;
      setComments((list) =>
        list.map((c) => {
          if (c.id !== commentId) return c;
          const current = c.labels || [];
          nextLabels = current.includes(label)
            ? current.filter((l) => l !== label)
            : [...current, label];
          return { ...c, labels: nextLabels };
        })
      );
      if (nextLabels === null) return;
      const res = await fetch(`${API}/api/comments/${commentId}/labels`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ labels: nextLabels, author }),
      });
      if (!res.ok) return;
      const updated = await res.json();
      setComments((list) => list.map((c) => (c.id === commentId ? updated : c)));
    },
    [ensureAuthor]
  );

  const updateContent = useCallback(
    async (commentId, patch) => {
      const author = ensureAuthor();
      if (!author) return;
      // Optimistic
      setComments((list) =>
        list.map((c) => (c.id === commentId ? { ...c, ...patch } : c))
      );
      const res = await fetch(`${API}/api/comments/${commentId}/content`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ author, ...patch }),
      });
      if (!res.ok) return;
      const updated = await res.json();
      setComments((list) => list.map((c) => (c.id === commentId ? updated : c)));
    },
    [ensureAuthor]
  );

  const deleteComment = useCallback(async (commentId, authorId) => {
    const res = await fetch(
      `${API}/api/comments/${commentId}?author_id=${encodeURIComponent(authorId)}`,
      { method: "DELETE", headers: authHeaders() }
    );
    if (!res.ok) return;
    setComments((list) => list.filter((c) => c.id !== commentId));
    setActiveId((id) => (id === commentId ? null : id));
  }, []);

  const deleteReply = useCallback(async (commentId, replyId, authorId) => {
    const res = await fetch(
      `${API}/api/comments/${commentId}/replies/${replyId}?author_id=${encodeURIComponent(authorId)}`,
      { method: "DELETE", headers: authHeaders() }
    );
    if (!res.ok) return;
    const updated = await res.json();
    setComments((list) => list.map((c) => (c.id === commentId ? updated : c)));
  }, []);

  const updateReply = useCallback(
    async (commentId, replyId, patch) => {
      const author = ensureAuthor();
      if (!author) return;
      // Optimistic update — patch the matching reply in-place. Server call
      // returns the canonical comment which then replaces the whole record.
      setComments((list) =>
        list.map((c) =>
          c.id === commentId
            ? {
                ...c,
                replies: (c.replies || []).map((r) =>
                  r.id === replyId ? { ...r, ...patch } : r
                ),
              }
            : c
        )
      );
      const res = await fetch(`${API}/api/comments/${commentId}/replies/${replyId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ author, ...patch }),
      });
      if (!res.ok) return;
      const updated = await res.json();
      setComments((list) => list.map((c) => (c.id === commentId ? updated : c)));
    },
    [ensureAuthor]
  );

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SOUND_KEY, next ? "1" : "0");
      } catch (_e) {
        // ignore quota
      }
      return next;
    });
  }, []);

  // ── Review-mode toggle ─────────────────────────────────────────────────
  // Entering review mode is done via a HARD RELOAD with `?review=1` in the
  // URL. Doing this in-place used to work most of the time but produced
  // rare races where Lenis, sticky-freeze, and animation-pause didn't all
  // land in the same frame — the reviewer would see comments overlaid on
  // a still-live PDP. A cold start eliminates every race by construction:
  // when the page comes back up with `?review=1`, every subsystem mounts
  // straight into review mode.
  //
  // We preserve the reviewer's scroll position via sessionStorage so the
  // reload is invisible except for a tiny fade-in loader.
  const REVIEW_SCROLL_KEY = "pinlayr.review.scroll";
  const [transitioning, setTransitioning] = useState(false);

  const setReviewModeSafe = useCallback((next) => {
    setReviewMode(next);
    try {
      const url = new URL(window.location.href);
      if (next) url.searchParams.set("review", "1");
      else url.searchParams.delete("review");
      const newSearch = url.searchParams.toString();
      const newUrl = url.pathname + (newSearch ? `?${newSearch}` : "") + url.hash;
      window.history.replaceState({}, "", newUrl);
    } catch (_e) {
      // ignore — URL update is a nicety, not required for the toggle to work
    }
  }, []);

  const enterReview = useCallback(() => {
    // Embed: flip UI on in-place — never freeze/reload the live host page.
    if (EMBED) { setReviewModeSafe(true); return; }
    // Capture scroll so the reload lands the reviewer exactly where they were
    try { sessionStorage.setItem(REVIEW_SCROLL_KEY, String(window.scrollY)); } catch (_e) { /* ignore */ }
    setTransitioning(true);
    // Give the loader one paint to fade in before we tear the page down
    requestAnimationFrame(() => {
      const url = new URL(window.location.href);
      url.searchParams.set("review", "1");
      window.location.href = url.toString();
    });
  }, [setReviewModeSafe]);

  const exitReview = useCallback(() => {
    // Embed: just hide the review UI in-place (no reload / no unfreeze needed).
    if (EMBED) { setActiveId(null); setReviewModeSafe(false); return; }
    // Same cold-start philosophy on the way back — a hard reload restores
    // Lenis + stickies + animations with zero chance of stale side-effects.
    try { sessionStorage.setItem(REVIEW_SCROLL_KEY, String(window.scrollY)); } catch (_e) { /* ignore */ }
    setTransitioning(true);
    requestAnimationFrame(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete("review");
      url.searchParams.delete("ticket");
      const newSearch = url.searchParams.toString();
      window.location.href = url.pathname + (newSearch ? `?${newSearch}` : "") + url.hash;
    });
  }, [setReviewModeSafe]);

  // On mount, if we're arriving from an enter/exit reload, restore the
  // captured scroll position after the page has painted its initial layout.
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem(REVIEW_SCROLL_KEY); } catch (_e) { /* ignore */ }
    if (raw == null) return;
    const y = parseInt(raw, 10);
    try { sessionStorage.removeItem(REVIEW_SCROLL_KEY); } catch (_e) { /* ignore */ }
    if (!Number.isFinite(y)) return;
    // Two frames: one for the freeze effect to un-stick stickies, one for
    // final layout, then scroll to the exact same page Y.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.scrollTo(0, y));
    });
    // Belt-and-suspenders: also try again after the freeze rAF cascade.
    const t = setTimeout(() => window.scrollTo(0, y), 300);
    return () => clearTimeout(t);
  }, []);

  // Freeze / unfreeze side-effect. Runs after the flag flips so the
  // browser lays out the page in review layout before pins re-anchor.
  // We re-run the freeze twice more on short delays to catch any elements
  // that mount late (image loaders, framer-motion components, async data).
  useEffect(() => {
    if (EMBED) return undefined;   // embed: never freeze the live host page
    if (reviewMode) {
      document.documentElement.classList.add("review-mode");
      const run = () => {
        unfreezePageStickies(frozenElsRef.current);
        frozenElsRef.current = freezePageStickies();
      };
      const raf = requestAnimationFrame(run);
      const t1 = setTimeout(run, 400);
      const t2 = setTimeout(run, 1400);
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    // Exiting review mode
    document.documentElement.classList.remove("review-mode");
    unfreezePageStickies(frozenElsRef.current);
    frozenElsRef.current = [];
    return undefined;
  }, [reviewMode]);

  // Also re-freeze on route change while in review mode so the newly
  // mounted page's stickies are captured too.
  useEffect(() => {
    if (EMBED) return;
    if (!reviewMode) return;
    // Wait for the new route to mount, then re-snapshot
    const id = requestAnimationFrame(() => {
      unfreezePageStickies(frozenElsRef.current);
      frozenElsRef.current = freezePageStickies();
    });
    return () => cancelAnimationFrame(id);
  }, [route, reviewMode]);

  // Build a de-duplicated author directory (across ALL comments) for @-mentions
  const knownAuthors = useMemo(() => {
    const map = new Map();
    for (const c of allComments) {
      map.set(c.author.id, c.author);
      for (const r of c.replies) map.set(r.author.id, r.author);
    }
    // Merge the project mention directory (workspace members + reviewers) so
    // authenticated members can be @-mentioned by stable userId even before
    // they've authored anything. Real authors take precedence.
    for (const d of directory) {
      if (!map.has(d.id)) map.set(d.id, { id: d.id, name: d.name, color: d.color || pickColor(d.name || d.id), type: d.type });
    }
    if (me) map.set(me.id, me);
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [allComments, me, directory]);

  // Ticket number belongs to the PROJECT: the backend assigns a persistent,
  // monotonic `ticketNumber` at creation time, so it never restarts per route,
  // reviewer, account or session. We use it directly. Only comments that lack a
  // stored number (e.g. the standalone /demo harness with no project) fall back
  // to per-route sequential numbering for display.
  const numberMap = useMemo(() => {
    const out = new Map();
    const missing = [];
    for (const c of allComments) {
      if (typeof c.ticketNumber === "number") out.set(c.id, c.ticketNumber);
      else missing.push(c);
    }
    const byRoute = new Map();
    for (const c of missing) {
      const arr = byRoute.get(c.route) || [];
      arr.push(c);
      byRoute.set(c.route, arr);
    }
    for (const [, arr] of byRoute) {
      arr.sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
      arr.forEach((c, i) => out.set(c.id, i + 1));
    }
    return out;
  }, [allComments]);
  const numberFor = useCallback((commentId) => numberMap.get(commentId) || null, [numberMap]);

  const value = useMemo(
    () => ({
      me,
      setMe,
      saveName,
      namePromptOpen,
      openNamePrompt: () => setNamePromptOpen(true),
      closeNamePrompt: () => setNamePromptOpen(false),
      route,
      comments,
      allComments,
      loading,
      mode,
      setMode,
      activeId,
      setActiveId,
      showSidebar,
      setShowSidebar,
      showDevicePreview,
      setShowDevicePreview,
      addComment,
      addReply,
      updateAnchor,
      setStatus,
      updateContent,
      deleteComment,
      deleteReply,
      updateReply,
      currentViewport,
      viewport,
      refresh: fetchComments,
      hasUnread,
      mentionsMe,
      unreadCount,
      unreadCountAllRoutes,
      mentionedCount,
      markSeen,
      soundEnabled,
      toggleSound,
      knownAuthors,
      numberFor,
      reviewMode,
      enterReview,
      exitReview,
      transitioning,
      role: readRole() || "client",
      toggleLabel,
    }),
    [
      me,
      saveName,
      namePromptOpen,
      route,
      comments,
      allComments,
      loading,
      mode,
      activeId,
      showSidebar,
      showDevicePreview,
      addComment,
      addReply,
      updateAnchor,
      setStatus,
      updateContent,
      deleteComment,
      deleteReply,
      updateReply,
      fetchComments,
      hasUnread,
      mentionsMe,
      unreadCount,
      unreadCountAllRoutes,
      mentionedCount,
      markSeen,
      soundEnabled,
      toggleSound,
      knownAuthors,
      viewport,
      numberFor,
      reviewMode,
      enterReview,
      exitReview,
      transitioning,
      toggleLabel,
    ]
  );

  // Disable Comments on the gate itself + inside the mobile-preview iframe
  const suppressed = HIDE_ROUTES.includes(route);
  return (
    <CommentsCtx.Provider value={value}>
      {children}
      {!suppressed}
    </CommentsCtx.Provider>
  );
};
